import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import { Plus, Edit2, Trash2, Send, Clock, Tag, X } from 'lucide-react';

const TimeEntries = () => {
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkTagId, setBulkTagId] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const tagInputRef = useRef(null);
  const [filters, setFilters] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
  });
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    start_time: '08:00',
    end_time: '17:00',
    break_minutes: 30,
    project_id: '',
    description: '',
    tag_ids: [],
  });

  const fetchEntries = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      const res = await api.get(`/time-entries?${params}`);
      setEntries(res.data);
    } catch (error) {
      console.error('Fehler:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchTags = useCallback(async () => {
    try {
      const res = await api.get('/tags');
      setAllTags(res.data);
    } catch (error) {
      console.error('Tags-Fehler:', error);
    }
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await api.get('/projects');
        setProjects(res.data);
      } catch (error) {
        console.error('Projekte-Fehler:', error);
      }
    };
    fetchProjects();
    fetchTags();
  }, [fetchTags]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const openModal = (entry = null) => {
    setWarnings([]);
    setTagInput('');
    setShowTagSuggestions(false);
    if (entry) {
      setEditEntry(entry);
      setForm({
        date: entry.date.split('T')[0],
        start_time: entry.start_time?.substring(0, 5),
        end_time: entry.end_time?.substring(0, 5),
        break_minutes: entry.break_minutes,
        project_id: entry.project_id || '',
        description: entry.description || '',
        tag_ids: (entry.tags || []).map(t => t.id),
      });
    } else {
      setEditEntry(null);
      setForm({
        date: new Date().toISOString().split('T')[0],
        start_time: '08:00',
        end_time: '17:00',
        break_minutes: 30,
        project_id: '',
        description: '',
        tag_ids: [],
      });
    }
    setShowModal(true);
  };

  const handleAddTagToForm = async (tag) => {
    if (!form.tag_ids.includes(tag.id)) {
      setForm({ ...form, tag_ids: [...form.tag_ids, tag.id] });
    }
    setTagInput('');
    setShowTagSuggestions(false);
  };

  const handleCreateAndAddTag = async (name) => {
    try {
      const res = await api.post('/tags', { name });
      const newTag = res.data;
      setAllTags(prev => [...prev, newTag].sort((a, b) => a.name.localeCompare(b.name)));
      setForm({ ...form, tag_ids: [...form.tag_ids, newTag.id] });
    } catch (error) {
      console.error('Tag-Erstellung fehlgeschlagen:', error);
    }
    setTagInput('');
    setShowTagSuggestions(false);
  };

  const handleRemoveTagFromForm = (tagId) => {
    setForm({ ...form, tag_ids: form.tag_ids.filter(id => id !== tagId) });
  };

  const handleBulkAssign = async () => {
    if (!bulkTagId || selectedIds.length === 0) return;
    try {
      await api.post('/tags/bulk-assign', { time_entry_ids: selectedIds, tag_ids: [bulkTagId] });
      setSelectedIds([]);
      setBulkTagId('');
      fetchEntries();
    } catch (error) {
      alert('Fehler beim Zuweisen der Tags.');
    }
  };

  const handleBulkRemove = async () => {
    if (!bulkTagId || selectedIds.length === 0) return;
    try {
      await api.post('/tags/bulk-remove', { time_entry_ids: selectedIds, tag_ids: [bulkTagId] });
      setSelectedIds([]);
      setBulkTagId('');
      fetchEntries();
    } catch (error) {
      alert('Fehler beim Entfernen der Tags.');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === entries.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(entries.map(e => e.id));
    }
  };

  const filteredTagSuggestions = allTags.filter(t =>
    !form.tag_ids.includes(t.id) &&
    t.name.toLowerCase().includes(tagInput.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        project_id: form.project_id || null,
        break_minutes: parseInt(form.break_minutes) || 0,
      };

      let res;
      if (editEntry) {
        res = await api.put(`/time-entries/${editEntry.id}`, payload);
      } else {
        res = await api.post('/time-entries', payload);
      }

      if (res.data.warnings?.length > 0) {
        setWarnings(res.data.warnings);
      } else {
        setShowModal(false);
      }

      fetchEntries();
      fetchTags();
    } catch (error) {
      alert(error.response?.data?.error || 'Fehler beim Speichern.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Zeiteintrag wirklich löschen?')) return;
    try {
      await api.delete(`/time-entries/${id}`);
      fetchEntries();
    } catch (error) {
      alert(error.response?.data?.error || 'Fehler beim Löschen.');
    }
  };

  const handleSubmitEntry = async (id) => {
    try {
      await api.put(`/time-entries/${id}/status`, { status: 'submitted' });
      fetchEntries();
    } catch (error) {
      alert(error.response?.data?.error || 'Fehler beim Einreichen.');
    }
  };

  const calcHours = (start, end, breakMin) => {
    if (!start || !end) return '-';
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const total = (eh * 60 + em - sh * 60 - sm - (breakMin || 0)) / 60;
    return total > 0 ? total.toFixed(2) : '0.00';
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Laden...</div>;

  return (
    <div>
      <div className="page-header">
        <div className="page-header-actions">
          <div>
            <h2>Zeiterfassung</h2>
            <p>Erfassen und verwalten Sie Ihre Arbeitszeiten</p>
          </div>
          <button className="btn btn-primary" onClick={() => openModal()}>
            <Plus size={16} /> Neuer Eintrag
          </button>
        </div>
      </div>

      <div className="filters">
        <div className="form-group">
          <label>Von</label>
          <input type="date" className="form-control" value={filters.start_date}
            onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Bis</label>
          <input type="date" className="form-control" value={filters.end_date}
            onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} />
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="bulk-tag-bar">
          <span className="bulk-count">{selectedIds.length} ausgewählt</span>
          <select value={bulkTagId} onChange={(e) => setBulkTagId(e.target.value)}>
            <option value="">Tag wählen...</option>
            {allTags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button className="btn btn-success btn-sm" onClick={handleBulkAssign} disabled={!bulkTagId}>
            <Tag size={12} /> Zuweisen
          </button>
          <button className="btn btn-danger btn-sm" onClick={handleBulkRemove} disabled={!bulkTagId}>
            <X size={12} /> Entfernen
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setSelectedIds([])}>
            Auswahl aufheben
          </button>
        </div>
      )}

      <div className="card">
        <div className="table-container">
          {entries.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th style={{ width: 36 }}><input type="checkbox" className="row-checkbox" onChange={toggleSelectAll} checked={selectedIds.length === entries.length && entries.length > 0} /></th>
                  <th>Datum</th>
                  <th>Start</th>
                  <th>Ende</th>
                  <th>Pause</th>
                  <th>Stunden</th>
                  <th>Projekt</th>
                  <th>Tags</th>
                  <th>Beschreibung</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id} style={selectedIds.includes(entry.id) ? { background: '#f0f4ff' } : {}}>
                    <td><input type="checkbox" className="row-checkbox" checked={selectedIds.includes(entry.id)} onChange={() => toggleSelect(entry.id)} /></td>
                    <td>{new Date(entry.date).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}</td>
                    <td>{entry.start_time?.substring(0, 5)}</td>
                    <td>{entry.end_time?.substring(0, 5)}</td>
                    <td>{entry.break_minutes} Min</td>
                    <td><strong>{calcHours(entry.start_time, entry.end_time, entry.break_minutes)}</strong></td>
                    <td>{entry.project_name || '-'}</td>
                    <td>
                      <div className="tags-cell">
                        {(entry.tags || []).map(tag => (
                          <span key={tag.id} className="tag" style={{ background: tag.color + '20', color: tag.color }}>
                            <span className="tag-dot" style={{ background: tag.color }} />
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entry.description || '-'}
                    </td>
                    <td><span className={`badge badge-${entry.status}`}>{entry.status}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {entry.status === 'draft' && (
                          <>
                            <button className="btn btn-secondary btn-sm btn-icon" onClick={() => openModal(entry)} title="Bearbeiten">
                              <Edit2 size={14} />
                            </button>
                            <button className="btn btn-secondary btn-sm btn-icon" onClick={() => handleSubmitEntry(entry.id)} title="Einreichen">
                              <Send size={14} />
                            </button>
                            <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(entry.id)} title="Löschen">
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <Clock size={40} />
              <p>Keine Zeiteinträge im gewählten Zeitraum.</p>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editEntry ? 'Eintrag bearbeiten' : 'Neuer Zeiteintrag'}</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {warnings.length > 0 && (
                  <div className="warning-box">
                    <strong>Hinweise zum Arbeitszeitgesetz:</strong>
                    <ul>{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                  </div>
                )}
                <div className="form-group">
                  <label>Datum</label>
                  <input type="date" className="form-control" value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Startzeit</label>
                    <input type="time" className="form-control" value={form.start_time}
                      onChange={(e) => setForm({ ...form, start_time: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Endzeit</label>
                    <input type="time" className="form-control" value={form.end_time}
                      onChange={(e) => setForm({ ...form, end_time: e.target.value })} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Pause (Minuten)</label>
                    <input type="number" className="form-control" value={form.break_minutes} min="0" max="480"
                      onChange={(e) => setForm({ ...form, break_minutes: e.target.value })} />
                    <span className="form-hint">Min. 30 Min bei &gt;6h, 45 Min bei &gt;9h</span>
                  </div>
                  <div className="form-group">
                    <label>Projekt</label>
                    <select className="form-control" value={form.project_id}
                      onChange={(e) => setForm({ ...form, project_id: e.target.value })}>
                      <option value="">Kein Projekt</option>
                      {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Beschreibung</label>
                  <textarea className="form-control" value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Tätigkeit beschreiben..." />
                </div>
                <div className="form-group">
                  <label>Tags</label>
                  <div className="tag-input-wrapper">
                    <input
                      ref={tagInputRef}
                      type="text"
                      className="tag-input-field"
                      value={tagInput}
                      onChange={(e) => { setTagInput(e.target.value); setShowTagSuggestions(true); }}
                      onFocus={() => setShowTagSuggestions(true)}
                      placeholder="Tag suchen oder erstellen..."
                    />
                    {showTagSuggestions && (tagInput || filteredTagSuggestions.length > 0) && (
                      <div className="tag-suggestions">
                        {filteredTagSuggestions.map(t => (
                          <div key={t.id} className="tag-suggestion-item" onClick={() => handleAddTagToForm(t)}>
                            <span className="tag-dot" style={{ background: t.color }} />
                            {t.name}
                          </div>
                        ))}
                        {tagInput.trim() && !allTags.find(t => t.name.toLowerCase() === tagInput.trim().toLowerCase()) && (
                          <div className="tag-suggestion-item create-new" onClick={() => handleCreateAndAddTag(tagInput.trim())}>
                            + "{tagInput.trim()}" erstellen
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {form.tag_ids.length > 0 && (
                    <div className="selected-tags">
                      {form.tag_ids.map(id => {
                        const tag = allTags.find(t => t.id === id);
                        if (!tag) return null;
                        return (
                          <span key={id} className="tag" style={{ background: tag.color + '20', color: tag.color }}>
                            <span className="tag-dot" style={{ background: tag.color }} />
                            {tag.name}
                            <button type="button" className="tag-remove" onClick={() => handleRemoveTagFromForm(id)}>×</button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Abbrechen</button>
                <button type="submit" className="btn btn-primary">
                  {editEntry ? 'Speichern' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeEntries;
